import Module from './module';
import exceptions from '~/lib/exceptions';
import rest from '~/lib/rest';
import lang from '~/lib/lang';

/**
 * Build the storage key that remembers a table's filters.
 *
 * The key is the route name and the collection URL together, so a remembered
 * filter belongs to a page rather than to an entity type. The dashboard's
 * Recent Jobs and the job index both list '/jobs', and each keeps its own
 * selection: filtering one leaves the other alone.
 *
 * Route name is the page's identity -- it survives a change of URL parameters,
 * and it is what routes are already named by. The collection URL stays in the
 * key because one page can show two filterable collections: CustomerIndexPage
 * runs customer/datatable and customer/companies side by side, and the URL is
 * what tells them apart. Modules sharing both a route and a URL share a key by
 * design; they are the same collection on the same page.
 *
 * A detail page keys on the route alone, not the record -- 'customer:detail'
 * covers every customer. The preference is about the kind of page, not about
 * which record happens to be open.
 *
 * @param {String} route  the current route name
 * @param {String} url
 * @return {String|null} null when the table has no url or no named route yet
 */
export function filterStorageKey (route, url) {
  return route && url ? `datatable:filters:${route}:${url}` : null;
}

export class DataTableModule extends Module {

  /**
   * A list of column definitions. A column definition is an object that contains
   * these properties:
   *
   *    label {String} the table column heading
   *    value {Function} a function that accepts a row object and returns a String
   *
   * @readonly
   * @property
   * @type {Array}
   */
  get columns () {
    return this.option('columns');
  }

  get defaultQueryParams () {
    let defaults = {
      sorts: {id: 'desc'},
      filters: {},
      includes: [],
      paging: {offset: 0, limit: 10},
    };
    return lang.object.merge(defaults, this.option('query', {}));
  }

  /**
   * @inheritdoc
   */
  get state () {
    return {
      columns: this.columns,
      rows: [],
      page: 1,   // current page number
      total: 0,  // total number of records in the dataset
      url: null, // the REST collection resource path
      query: this.defaultQueryParams,

      // Filters the page set to scope this table to a record, kept apart
      // from the filters in query.filters that the user chose. Both go into
      // the request; only query.filters is remembered. See setScope.
      scope: {},
    };
  }

  /**
   * @inheritdoc
   */
  get getters () {
    return {
      ...this.option('getters', {}),
      columns: state => state.columns || [],
      rows: state => state.rows || [],
      page: state => state.page,
      limit: state => Number(state.query.paging.limit),
      total: state => Number(state.total || 0),
      query: state => state.query,
      filters: state => state.query.filters || {},
      scope: state => state.scope || {},
      url: state => state.url,
    };
  }

  /**
   * @inheritdoc
   */
  get mutations () {
    return {
      ...this.option('mutations', {}),
      page: (state, page) => state.page = page,
      columns: (state, columns) => state.columns = columns,
      rows: (state, rows) => state.rows = rows,
      limit: (state, limit) => state.query.paging.limit = limit,
      total: (state, total) => state.total = total,
      query: (state, query) => state.query = query,
      scope: (state, scope) => state.scope = scope,
      url: (state, url) => state.url = url,
    };
  }

  /**
   * @inheritdoc
   */
  get actions () {
    let services = this.services;
    let api = services.get('api');
    let storage = services.get('storage');
    let router = services.get('router');

    /**
     * The name of the route being displayed, or null before one resolves.
     *
     * Read at the moment a filter is stored or restored rather than captured
     * once: a single module instance serves every page that lists the
     * resource, so the answer changes as the user navigates.
     *
     * @return {String|null}
     */
    let currentRouteName = () => {
      let current = router && router.currentRoute;
      return (current && current.name) || null;
    };

    return {

      ...this.option('actions', {}),

      /**
       * Reset the table to its default state.
       *
       * Clears rows and total along with the query. One module instance
       * serves every page that lists the resource, so whatever the last page
       * fetched is still in state when the next one initializes. Leaving the
       * rows behind means a page renders the previous page's records until
       * its own fetch returns -- setPage is the only action that issues a
       * request, and it runs well after the first paint.
       */
      initialize: async (store, params={}) => {
        let {url, query} = params;
        store.commit('url', url || this.option('url'));
        store.commit('query', this.defaultQueryParams);
        store.commit('rows', []);
        store.commit('total', 0);
        store.commit('page', 1);
        store.commit('scope', {});
        if (query) {
          // Filters passed at initialize are the page's own scoping -- the
          // customer whose jobs this list shows -- so they go to setScope
          // rather than into query.filters, which load() overwrites when it
          // restores a remembered filter. Everything else in the query
          // (sorts, includes, paging) takes its usual path.
          let {filters, ...rest} = query;
          if (filters) {
            store.dispatch('setScope', filters);
          }
          if (Object.keys(rest).length) {
            store.dispatch('setQuery', rest);
          }
        }
      },

      /**
       * Scope the table to a subset a page owns, such as one customer's
       * jobs.
       *
       * Scope is separate from query.filters because the two answer to
       * different owners. A page decides its scope and it is not
       * negotiable; query.filters holds what the user chose, and only that
       * is remembered between visits. Keeping them in one dictionary meant
       * a restored filter replaced the page's scoping outright, and the
       * list quietly widened to the whole collection.
       *
       * @param {Object} store
       * @param {Object} filters  the complete scope, replacing any prior one
       * @return void
       */
      setScope: (store, filters) => {
        store.commit('scope', {...filters});
      },

      load: async (store) => {
        // Restore any remembered filters before the first fetch. This runs
        // after initialize() has reset the query to defaults, which is the
        // only point where a stored filter survives.
        let storageKey = filterStorageKey(currentRouteName(), store.state.url);
        let remembered = storageKey ? storage.get(storageKey) : null;
        if (remembered && Object.keys(remembered).length) {
          await store.dispatch('setQuery', {filters: remembered});
        }
        let limit = Number(storage.get('paging:limit') || 10);
        await store.dispatch('setLimit', limit);
      },

      /**
       * Set a single filter and reload the table.
       *
       * Pass an empty array (or null) to drop the filter entirely, so that a
       * cleared filter and a filter that was never set are the same state.
       *
       * @async
       * @param {Object} store
       * @param {Object} params  {key, value, remember}
       * @return void
       */
      setFilter: async (store, {key, value, remember = true}) => {
        let filters = {...store.state.query.filters};
        let empty = value === null || value === undefined ||
          (Array.isArray(value) && !value.length);

        if (empty) {
          delete filters[key];
        }
        else {
          filters[key] = value;
        }

        await store.dispatch('setQuery', {filters});

        // Remember only the keys the user chose, never the whole filter
        // dictionary. Pages scope this same module with filters of their own
        // -- CustomerDetailPage sets match_customers on /jobs -- and those
        // belong to the page's own wiring, not to a user preference. Storing
        // one would pin a stale record id onto every later visit to the page.
        let storageKey = filterStorageKey(currentRouteName(), store.state.url);
        if (remember && storageKey) {
          let stored = storage.get(storageKey) || {};
          if (empty) {
            delete stored[key];
          }
          else {
            stored[key] = value;
          }

          if (Object.keys(stored).length) {
            storage.set(storageKey, stored);
          }
          else {
            storage.remove(storageKey);
          }
        }

        // setPage is the only action that issues a request, so changing a
        // filter does not refetch on its own. Page 1 rather than the current
        // page: the rows page 3 was showing may not be in the filtered set.
        await store.dispatch('setPage', 1);
      },

      /**
       * Update paging limit
       * @async
       * @param {Object} store
       * @param {Number} page
       * @return void
       */
      setLimit: async (store, limit) => {
        limit = Number(limit);
        storage.get('paging:limit', limit);
        await store.dispatch('setQuery', {paging: {limit}});
        await store.dispatch('setPage', 1);
      },

      /**
       * Navigate to a datatable page
       * @async
       * @param {Object} store
       * @param {Number} page
       * @return void
       */
      setPage: async (store, page) => {
        store.commit('page', Number(page));

        let {state} = store;
        let limit = state.query.paging.limit;
        let offset = (state.page - 1) * limit;

        await store.dispatch('setQuery', {paging: {limit, offset}});

        // Build the request from both filter sets. Scope wins a key
        // collision: a page's own scoping is not something a remembered
        // preference may override. Neither dictionary is mutated -- scope
        // must survive the next restored filter, and query.filters is what
        // the status control reads back.
        let filters = {...state.query.filters, ...state.scope};
        let qs = String(new rest.Query({...state.query, filters}));
        let url = qs ? `${state.url}?${qs}` : state.url;
        let response = await api.get(url);
        let rows = lang.object.freeze(response.payload);
        let crange = response.headers.get('Content-Range');
        let {total} = rest.parse.contentRange(crange);

        store.commit('rows', rows);
        store.commit('total', total);
      },

      /**
       * Set query parameters
       * @async
       * @param {Object} params
       * @return void
       */
      setQuery: (store, params) => {
        let {parse, stringify} = JSON;
        let base = parse(stringify(store.state.query));
        let query = lang.object.merge(base, params);
        if (params.sorts) {
          query.sorts = {...params.sorts};
        }
        // Filters replace rather than merge, for the same reason sorts do.
        // lang.object.merge is lodash.merge, which merges arrays index by
        // index, so a merged filter can never shrink. Clearing is the case
        // that bites: merging [] over ['quoted'] leaves ['quoted'], and the
        // control would read "all statuses" while the list stayed filtered.
        //
        // The whole dictionary replaces, rather than spreading over the old
        // one. A caller that drops a key -- setFilter deletes it to clear a
        // filter -- passes a dictionary without that key, and spreading
        // cannot express a removal: the old key would survive underneath.
        // Callers therefore send the complete filter set they want.
        if (params.filters) {
          query.filters = {...params.filters};
        }

        store.commit('query', query);
      },

      setState: (store, mutations) => {
        Object.keys(this.mutations).map((key) => {
          if (mutations.hasOwnProperty(key)) {
            store.commit(key, mutations[key]);
          }
        });
      },
    };
  }
}


export default DataTableModule;

